/* Provides contexts and components for use with "flow" apps that show multiple "frames" (screens)
 * in sequence. */

import React, {createContext, useMemo, useCallback, useReducer} from 'react'
import {useOnUpdate} from "js/studios/shared/unredux/hooks"

export const FlowFramesCtx = createContext()
FlowFramesCtx.displayName = "FlowFramesCtx"

/* Wrapper component for moving between frames in the flow. Initially displays the first frame.
 * All frames in `frames` should have a `key` attr. If `frames` can change while component is
 * mounted, be careful to only change it when on a frame that will always be there. */
export function FlowFramesProvider({
  frames: framesProp,
  curFrame: curFrameProp, // optional key of current frame to show. Defaults to first frame if not provided.
  onChange, // optional callback for when frames change, e.g. for analytics
}) {
  /* Since parent component can change the frames, curFrameIdx only makes any sense in relation
   * to the frames at the time that index was determined, and navigations can be triggered
   * synchronously with data changes impacting frames before state changes here have settled the
   * frame changes, we put both the frames themselves & the calculated current frame index into a
   * reducer state. This approach keeps actions changing frames or current frame applied serially
   * and on consistent data.
   */
  const [{frames, curFrameIdx}, dispatch] = useReducer(({frames, curFrameIdx}, action) => {
    switch (action.type) {
      case "set-frames":
        return {frames: action.frames, curFrameIdx}
      case "prev-frame":
        return {frames, curFrameIdx: Math.max(curFrameIdx - 1, 0)}
      case "next-frame":
        return {frames, curFrameIdx: Math.min(curFrameIdx + 1, frames.length - 1)}
      case "goto-frame": {
        const idx = frames.findIndex(f => f.key === action.frame)
        if (idx < 0) {
          throw new Error("Invalid frame key")
        }
        return {frames, curFrameIdx: idx}
      }
      default:
        throw new Error("Invalid FlowFramesProvider reducer action")
    }
  }, null, () => ({
    frames: framesProp,
    curFrameIdx: curFrameProp ? frames.findIndex(f => f.key === curFrameProp) : 0
  }))

  const
    gotoPrevFrame = useCallback(ev => {
      ev?.preventDefault?.()
      dispatch({type: "prev-frame"})
    }, []),
    gotoNextFrame = useCallback(ev => {
      ev?.preventDefault?.()
      dispatch({type: "next-frame"})
    }, []),
    gotoFrame = useCallback(frameKey => {
      dispatch({type: "goto-frame", frame: frameKey})
    }, []),
    ctxVal = useMemo(
      () => ({frames, curFrameIdx, gotoPrevFrame, gotoNextFrame, gotoFrame}),
      [frames, curFrameIdx, gotoPrevFrame, gotoNextFrame, gotoFrame],
    )

  useOnUpdate(prevIdx => {
    if (!onChange) { return }
    onChange({prevIdx, prevFrame: frames[prevIdx], curFrame: frames[curFrameIdx], curIdx: curFrameIdx})
  }, [curFrameIdx])

  // these are useOnUpdate even though we don't use the prev value because useEffect would also
  // run on mount, which we don't want.
  useOnUpdate(_prevFramesProp => {
    dispatch({type: "set-frames", frames: framesProp})
  }, [framesProp])

  useOnUpdate(_prevCurFrameProp => {
    if (curFrameProp) {
      dispatch({type: "goto-frame", frame: curFrameProp})
    }
  }, [curFrameProp])

  return <FlowFramesCtx.Provider value={ctxVal}>{frames[curFrameIdx]}</FlowFramesCtx.Provider>
}
